import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";
import { Task } from "@/api.types";

// New interface for task filtering and pagination
export interface TasksQueryParams {
  page?: number;
  limit?: number | "all"; // Allow "all" as a valid value
  status?: string;
  type?: string;
  tripId?: string | null; // Use null instead of "null" string
  depotId?: string;
  operatorId?: string;
  orderId?: string;
  orderNumber?: string;
  search?: string;
}

export const taskSearchQueries = {
  activePickupTasks: {
    limit: "all" as const,
    status: "PENDING,IN_PROGRESS",
    type: "PICKUP",
    tripId: "null",
  },
  activeDeliveryTasks: {
    limit: "all" as const,
    status: "PENDING,IN_PROGRESS",
    type: "DELIVERY",
    tripId: "null",
  },
  activeTasks: {
    limit: "all" as const,
    status: "PENDING,IN_PROGRESS",
    type: "PICKUP,DELIVERY,RETURN",
    tripId: "null",
  },
};

export const taskQueries = {
  activePickupTasks: () =>
    taskQueries.tasksSearchQueryBuilder(taskSearchQueries.activePickupTasks),
  activeDeliveryTasks: () =>
    taskQueries.tasksSearchQueryBuilder(taskSearchQueries.activeDeliveryTasks),
  activeTasks: () =>
    taskQueries.tasksSearchQueryBuilder(taskSearchQueries.activeTasks),
  taskIdQueryBuilder: (taskId: string) => ["tasks", taskId],
  tasksSearchQueryBuilder: (params: TasksQueryParams) => [
    "tasks",
    {
      ...params,
    },
  ],
};

// Define the input type for creating a task, based on the backend schema
export interface CreateTaskInput {
  type:
    | "PICKUP"
    | "DELIVERY"
    | "DEPOT_RECEIVE"
    | "DEPOT_SORT"
    | "DEPOT_DISPATCH"
    | "RETURN";
  status?: "PENDING" | "IN_PROGRESS" | "COMPLETED" | "FAILED" | "CANCELLED";
  orderId: string;
  depotId: string;
  parcelId: string;
  description?: string;
  tripId?: string;
  operatorId?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    province: string;
    country: string;
    postalCode?: string;
    location: {
      lat: number;
      lng: number;
    };
    plusCode?: string;
  };
}

export const useUpdateTaskStatus = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: Error) => void;
}) => {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: { taskId: string; status: string }) => {
      const { data } = await axios.patch<Task>(
        `${import.meta.env.VITE_API_URL}/api/v1/tasks/${values.taskId}/status`,
        { status: values.status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: taskQueries.taskIdQueryBuilder(data.id),
      });
      onSuccess();
    },
    onError: (error) => {
      onError(error);
    },
  });
};

// Create assign tasks mutation
export function useAssignTasksToTrip({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: { tripId: string; taskIds: string[] }) => {
      const { data } = await axios.patch<{
        message: string;
        taskIds: string[];
        tripIds: string[];
      }>(
        `${import.meta.env.VITE_API_URL}/api/v1/trips/${values.tripId}/tasks/assign`,
        { taskIds: values.taskIds },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async (data) => {
      if (data.taskIds.length > 0) {
        const taskIds = data.taskIds;
        await Promise.all(
          taskIds.map((taskId) =>
            queryClient.invalidateQueries({
              queryKey: taskQueries.taskIdQueryBuilder(taskId),
            })
          )
        );
      }
      if (data.tripIds.length > 0) {
        const tripIds = data.tripIds;
        await Promise.all(
          tripIds.map((tripId) =>
            queryClient.invalidateQueries({ queryKey: ["trips", tripId] })
          )
        );
      }

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: taskQueries.activePickupTasks(),
        }),
        queryClient.invalidateQueries({
          queryKey: taskQueries.activeDeliveryTasks(),
        }),
        queryClient.invalidateQueries({
          queryKey: ["all-waypoints"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["trips", { status: "PLANNED,ACTIVE,COMPLETED" }],
        }),
      ]);
      onSuccess();
    },
    onError: (error) => {
      onError(error);
    },
  });
}

export function useUnassignTasksFromTrip({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: { tripId: string; taskIds: string[] }) => {
      const { data } = await axios.delete<{
        message: string;
        taskIds: string[];
        tripIds: string[];
      }>(
        `${import.meta.env.VITE_API_URL}/api/v1/trips/${values.tripId}/tasks/unassign`,
        {
          data: { taskIds: values.taskIds },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async (data) => {
      if (data.taskIds.length > 0) {
        const taskIds = data.taskIds;
        await Promise.all(
          taskIds.map((taskId) =>
            queryClient.invalidateQueries({
              queryKey: taskQueries.taskIdQueryBuilder(taskId),
            })
          )
        );
      }

      if (data.tripIds.length > 0) {
        const tripIds = data.tripIds;
        await Promise.all(
          tripIds.map((tripId) =>
            queryClient.invalidateQueries({ queryKey: ["trips", tripId] })
          )
        );
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["all-waypoints"] }),
        queryClient.invalidateQueries({
          queryKey: taskQueries.activePickupTasks(),
        }),
        queryClient.invalidateQueries({
          queryKey: taskQueries.activeDeliveryTasks(),
        }),
      ]);
      onSuccess();
    },
    onError: (error) => {
      onError(error);
    },
  });
}

// Updated hook with support for pagination, filtering, and search
export const useTasks = (params: TasksQueryParams = {}) => {
  const { token } = useSession();
  const {
    page = 1,
    limit = 100,
    status,
    type,
    tripId,
    depotId,
    operatorId,
    orderId,
    orderNumber,
    search,
  } = params;

  return useQuery<{
    data: Task[];
    metadata: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }>({
    queryKey: taskQueries.tasksSearchQueryBuilder({
      page,
      limit,
      status,
      type,
      tripId,
      depotId,
      operatorId,
      orderId,
      orderNumber,
      search,
    }),
    queryFn: async () => {
      // Build query string from parameters
      const queryParams = new URLSearchParams();
      if (page) queryParams.append("page", page.toString());

      // Handle the special "all" limit case
      if (limit === "all") {
        queryParams.append("limit", "all");
      } else if (limit) {
        queryParams.append("limit", limit.toString());
      }

      if (status) queryParams.append("status", status);
      if (type) queryParams.append("type", type);
      if (tripId) queryParams.append("tripId", tripId);
      if (depotId) queryParams.append("depotId", depotId);
      if (operatorId) queryParams.append("operatorId", operatorId);
      if (orderId) queryParams.append("orderId", orderId);
      if (orderNumber) queryParams.append("orderNumber", orderNumber);
      if (search) queryParams.append("search", search);

      const { data } = await axios.get<{ data: Task[]; metadata: any }>(
        `${import.meta.env.VITE_API_URL}/api/v1/tasks?${queryParams.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    enabled: !!token,
  });
};

// New hook for creating tasks
export function useCreateTask({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: Task) => void;
  onError?: (error: Error) => void;
} = {}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: CreateTaskInput) => {
      const { data } = await axios.post<Task>(
        `${import.meta.env.VITE_API_URL}/api/v1/depots/${values.depotId}/tasks/create`,
        values,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: taskQueries.activePickupTasks(),
      });
      await queryClient.invalidateQueries({
        queryKey: taskQueries.activeDeliveryTasks(),
      });

      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      if (onError) onError(error);
    },
  });
}

// Utility function to filter tasks by map bounds and other criteria
export function filterTasksByBounds(
  tasks: Task[],
  bounds: google.maps.LatLngBounds,
  options: {
    showPickups: boolean;
    showDeliveries: boolean;
    showTrips: boolean;
  }
): Task[] {
  const { showPickups, showDeliveries, showTrips } = options;

  return tasks.filter((task) => {
    if (!task.address?.location) return false;

    // Check if task is within bounds
    const taskLatLng = new google.maps.LatLng(
      task.address.location.lat,
      task.address.location.lng
    );
    if (!bounds.contains(taskLatLng)) return false;

    // Apply toggle filters
    if (!showPickups && task.type === "PICKUP") return false;
    if (!showDeliveries && task.type === "DELIVERY") return false;
    if (!showTrips && task.tripId) return false;
    if (task.status !== "PENDING" && task.status !== "IN_PROGRESS")
      return false;
    if (task.tripId) return false;

    return true;
  });
}
